import apiClient from '@/lib/apiClient';
import type { EvalTableRow } from '@knitknot/shared/components/ui/eval-table';
import type {
  ListPlaybooksParams,
  PlaybookArtifact,
  PlaybookDetail,
  PlaybookCitationStamp,
  PlaybookListItem,
  PlaybookPortfolio,
  PlaybookRoadmapResponse,
  PlaybookStatus,
  PlaybookTracking,
  PlaybookTrackingBaseline,
  PlaybookType,
  TrackedPromptBaselineFields,
} from '@/types/playbooks';

/** One tracked-prompt eval row: EvalTable-shaped + the W2-E at-ship baseline
 *  fields (frozen-cohort run window) for the measured-state Result-flip table. */
export type PlaybookTrackedPromptRow = EvalTableRow & TrackedPromptBaselineFields;

/** `GET .../playbooks/{id}/prompts` response — the buyer questions a play is
 *  measured against, EvalTable-shaped (same row shape as `/evaluations/by-coverage`).
 *  `tracking.phase` labels the rows (baseline vs measuring); `baseline` carries
 *  the frozen at-ship cohort summary for shipped plays (W2-E). */
export interface PlaybookPromptsResponse {
  rows: PlaybookTrackedPromptRow[];
  total: number;
  refusal_count: number;
  filters_applied: Record<string, unknown>;
  tracking?: PlaybookTracking | null;
  baseline?: PlaybookTrackingBaseline | null;
}

/** Term-seeded create-brief payload (DR12 composer → the create endpoint). The
 *  planner's `create_playbook` operator accepts `title`/`hypothesis`/`verb` +
 *  a `plan` blob; the composer weaves the retrieval-gap seed into `plan.seed`. */
export interface CreatePlaybookBody {
  title: string;
  hypothesis: string;
  playbook_type: PlaybookType;
  verb: 'create';
  plan?: Record<string, unknown>;
}

function _toQueryString(params: ListPlaybooksParams): string {
  // The console reads the current plan plus legacy implementation history.
  const parts: string[] = [];
  for (const s of params.status ?? []) parts.push(`status=${encodeURIComponent(s)}`);
  for (const reason of params.dismissed_reason ?? []) {
    parts.push(`dismissed_reason=${encodeURIComponent(reason)}`);
  }
  for (const t of params.playbook_type ?? []) parts.push(`playbook_type=${encodeURIComponent(t)}`);
  for (const r of params.run_id ?? []) parts.push(`run_id=${encodeURIComponent(r)}`);
  for (const id of params.playbook_id ?? []) parts.push(`playbook_id=${encodeURIComponent(id)}`);
  if (params.product_id) parts.push(`product_id=${encodeURIComponent(params.product_id)}`);
  if (params.family) parts.push(`family=${encodeURIComponent(params.family)}`);
  if (params.light) parts.push('light=true');
  if (params.include_citations === false) parts.push('include_citations=false');
  if (params.limit != null) parts.push(`limit=${encodeURIComponent(params.limit)}`);
  if (params.offset != null) parts.push(`offset=${encodeURIComponent(params.offset)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const playbookService = {
  list(workspaceId: string, params: ListPlaybooksParams = {}): Promise<PlaybookListItem[]> {
    return apiClient.get(`/workspaces/${workspaceId}/playbooks${_toQueryString(params)}`);
  },

  citations(workspaceId: string, playbookIds: string[]): Promise<Record<string, PlaybookCitationStamp>> {
    return apiClient.post(`/workspaces/${workspaceId}/playbooks/citations`, { playbook_ids: playbookIds });
  },

  get(workspaceId: string, playbookId: string): Promise<PlaybookDetail> {
    return apiClient.get(`/workspaces/${workspaceId}/playbooks/${playbookId}`);
  },

  /** Buyer questions a play is measured against — EvalTable-shaped rows.
   *  Empty/create-verb plays return `{ rows: [], total: 0, ... }`. */
  prompts(workspaceId: string, playbookId: string): Promise<PlaybookPromptsResponse> {
    return apiClient.get(`/workspaces/${workspaceId}/playbooks/${playbookId}/prompts`);
  },

  /** Mint a single proposed playbook from a hand-seeded brief (DR12 term CTA).
   *  NOTE: the matching `POST /workspaces/{ws}/playbooks` endpoint is not yet on
   *  the branch — see the composer's error handling + the slice report. */
  create(workspaceId: string, body: CreatePlaybookBody): Promise<PlaybookDetail> {
    return apiClient.post(`/workspaces/${workspaceId}/playbooks`, body);
  },

  portfolio(workspaceId: string): Promise<PlaybookPortfolio> {
    return apiClient.get(`/workspaces/${workspaceId}/playbooks/portfolio`);
  },

  roadmap(workspaceId: string): Promise<PlaybookRoadmapResponse> {
    return apiClient.get(`/workspaces/${workspaceId}/playbooks/roadmap`);
  },

  generateDiscovery(
    workspaceId: string,
    limit = 12,
  ): Promise<{ checked: number; queued: number; job_ids: string[] }> {
    return apiClient.post(`/workspaces/${workspaceId}/playbooks/discovery/generate`, { limit });
  },

  update(
    workspaceId: string,
    playbookId: string,
    body: Partial<{
      title: string;
      hypothesis: string;
      artifact: PlaybookArtifact;
      status: PlaybookStatus;
      shipped_url: string | null;
      dismissed_reason: string;
      dismissed_note: string | null;
      if_updated_at: string | null;
    }>,
  ): Promise<PlaybookDetail> {
    return apiClient.patch(`/workspaces/${workspaceId}/playbooks/${playbookId}`, body);
  },

  restore(workspaceId: string, playbookId: string): Promise<PlaybookDetail> {
    return apiClient.post(`/workspaces/${workspaceId}/playbooks/${playbookId}/restore`, {});
  },

  /** "Ignore" — a PERMANENT delete, and deliberately so (Max, 2026-07-28).
   *  Replaced `POST .../dismiss`, which parked the row in a `dismissed` status
   *  the planner then read as "never propose this again". Deleting cascades the
   *  issue links, so the issues the play covered return to the pool and the next
   *  generation is free to address them with a better play. Not undoable. */
  remove(workspaceId: string, playbookId: string): Promise<void> {
    return apiClient.delete(`/workspaces/${workspaceId}/playbooks/${playbookId}`);
  },

};
